import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import styles from "../styles.module.css";
import { ReactComponent as RowInfoSvg } from "../../../../assets/rowInfo.svg";
import { ReactComponent as NoRecordSvg } from "../../../../assets/norecord.svg";
import { ReactComponent as EditButtonSvg } from "../../../../assets/useredit.svg";
import { ReactComponent as OnlineDotSvg } from "../../../../assets/OnlineDot.svg";
import { ReactComponent as OfflineDotSvg } from "../../../../assets/offlinedot.svg";
import { ReactComponent as PendingDotSvg } from "../../../../assets/pendingdot.svg";
import { ReactComponent as ThumbsUpIconSvg } from "../../../../assets/thumbsupicon.svg";
import { ReactComponent as ThumbsDownIconSvg } from "../../../../assets/thumbsdownicon.svg";
import { ReactComponent as BinSvg } from "../../../../assets/Bin.svg";
import {
  IProduct,
  UpdateProductVisibility,
} from "../../../../app/models/product";
import { Image, Popup } from "semantic-ui-react";
import { observer } from "mobx-react-lite";
import { VendorsResponse } from "../../../../app/models/user";
import PaginationComponent from "../../../../app/common/pagination/PaginationComponent";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#32C36C",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {},
  borderColor: "white",
  "td, th": {
    border: 0,
  },
  // hide last border
  // "&:last-child td, &:last-child th": {
  //   border: 0,
  // },
}));

const isHttpUrl = (value: string) => /^https?:\/\//i.test(value);
const isDataUrl = (value: string) => /^data:/i.test(value);


const detectImageMime = (base64: string): string => {
  const sig = base64.slice(0, 24);
  if (sig.startsWith("iVBORw0KGgo")) return "image/png";
  if (sig.startsWith("/9j/")) return "image/jpeg";
  if (sig.startsWith("R0lGOD")) return "image/gif";
  if (sig.startsWith("UklGR")) return "image/webp";
  // Reasonable default for product photos when the signature is unrecognized
  return "image/png";
};

const resolveImageSrc = (value?: string | null): string | undefined => {
  if (!value || value === null) return undefined;

  // Already a normal URL or a properly formed data URI — use as-is
  if (isHttpUrl(value) || isDataUrl(value)) return value;

  // Otherwise treat it as raw base64 and build the data URI ourselves
  const cleaned = value.replace(/\s/g, "");
  const mime = detectImageMime(cleaned);
  return `data:${mime};base64,${cleaned}`;
};

// const src = '/images/wireframe/image.png'
interface Props {
  productList: IProduct[];
  deleteProduct: (creds: string) => Promise<VendorsResponse>;
  loading: boolean;
  changeProductVisibility: (creds: UpdateProductVisibility) => Promise<void>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}
const AllProducts = ({
  productList,
  deleteProduct,
  loading,
  changeProductVisibility,
  currentPage,
  setCurrentPage,
}: Props) => {
  const navigate = useNavigate();
  const [postsPerPage] = useState(7);

  // Get current posts
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentProductList = productList
    // .filter((val) => {
    //   if (searchTerm === "") {
    //     return val;
    //   } else if (
    //     val.productName != null &&
    //     val.productName.toLowerCase().includes(searchTerm.toLowerCase())
    //   ) {
    //     return val;
    //   }
    // })
    .slice(indexOfFirstPost, indexOfLastPost);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Handle Edit page route maintaining the current page
  const handleEditClick = (productId : any)=> {
    sessionStorage.setItem('currentPage', JSON.stringify(currentPage))
    navigate(productId)
  }

  const PLACEHOLDER_IMAGE_SRC =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="#F0F0F0"/>
      <g fill="none" stroke="#BDBDBD" stroke-width="4">
        <rect x="30" y="40" width="140" height="120" rx="6"/>
        <circle cx="70" cy="80" r="12"/>
        <path d="M30 140 L80 100 L110 125 L140 95 L170 140" />
      </g>
      <text x="100" y="180" font-family="Arial, sans-serif" font-size="12" fill="#9E9E9E" text-anchor="middle">
        No Image
      </text>
    </svg>
  `);
   useEffect(() => {
  //  console.log("productList", productList);
  }, [productList]);

  return (
    <>
      {productList.length > 0 ? (
        <TableContainer component={Paper} className={styles.PaperWrapper}>
          <Table
            sx={{ minWidth: 700 }}
            aria-label="customized table"
            className={styles.Table}
          >
            <TableHead className={styles.TableHead}>
              <TableRow>
                <StyledTableCell>No</StyledTableCell>
                <StyledTableCell>Image</StyledTableCell>
                <StyledTableCell>Product Name</StyledTableCell>
                <StyledTableCell>SKU</StyledTableCell>
                <StyledTableCell>Price</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Visibility</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody className={styles.TableBody}>
              {currentProductList.map((product, index) => {
                const imgSrc = resolveImageSrc(product.productPrimaryImageUrl) ?? PLACEHOLDER_IMAGE_SRC;

                return (
                <StyledTableRow key={index}>
                  <StyledTableCell component="th" scope="row">
                    {index + 1}
                  </StyledTableCell>
                  <StyledTableCell className={styles.imageCell}>
                    <Image src={imgSrc} size="mini" />
              
                    {/* {row.image} */}
                  </StyledTableCell>
                  <StyledTableCell>{product.productName}</StyledTableCell>
                  <StyledTableCell>{product.productCode}</StyledTableCell>
                  <StyledTableCell>
                    ₦ {product.productPrice.toLocaleString("en-US")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {product.status === "Approved" && (
                      <div className={styles.approved}>
                        <ThumbsUpIconSvg />
                        {product.status}
                      </div>
                    )}
                    {(product.status === "Disapproved" || product.status === "Declined") && (
                      <div className={styles.disapproved}>
                        <ThumbsDownIconSvg />
                        {product.status}
                      </div>
                    )}
                    {product.status === "Pending" && (
                      <div className={styles.pending}>
                        <PendingDotSvg />
                        {product.status}
                      </div>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {product.isEnable ? (
                      <div className={styles.online}>
                        <OnlineDotSvg />
                        <span>Online</span>
                      </div>
                    ) : (
                      <div className={styles.offline}>
                        <OfflineDotSvg />
                        <span>Offline</span>
                      </div>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>{product.dateCreated}</StyledTableCell>
                  <StyledTableCell>
                    {!loading && (
                      <Popup
                        style={{ width: 150 }}
                        trigger={<RowInfoSvg style={{ cursor: "pointer" }} />}
                        content={
                          <div className={styles.menu}>
                            <div
                              onClick={()=> handleEditClick(product.productId)}
                              style={{ cursor: "pointer" }}
                            >
                              <EditButtonSvg width={10} height={10} />
                              Edit
                            </div>
                            <hr />
                            <div
                              style={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                              }}
                              onClick={() =>
                                changeProductVisibility({
                                  productId: product.productId,
                                  isEnabled: product.isEnable ? false : true,
                                })
                              }
                            >
                              {!product.isEnable ? (
                                <OnlineDotSvg />
                              ) : (
                                <OfflineDotSvg />
                              )}
                              {!product.isEnable ? (
                                <div>Online</div>
                              ) : (
                                <div>Offline</div>
                              )}
                            </div>
                            <hr />
                            <div
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                deleteProduct(product.productId).catch((err) =>
                                  console.log(err)
                                )
                              }
                            >
                              <BinSvg />
                              Delete
                            </div>
                          </div>
                        }
                        on="click"
                        position="bottom center"
                      />
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className={styles.norecord}>
          <NoRecordSvg />
        </div>
      )}
      {/* {productList.length > 0 && (
        <div className={styles.pagination}>
          <Stack spacing={2}>
            <Pagination count={10} variant="outlined" shape="rounded" />
          </Stack>
        </div>
      )} */}
      {productList.length > 0 && (
        <div className={styles.pagination}>
          <PaginationComponent
            postsPerPage={postsPerPage}
            totalPosts={
              // .filter((val) => {
              //   if (searchTerm === "") {
              //     return val;
              //   } else if (
              //     val.productName != null &&
              //     val.productName.toLowerCase().includes(searchTerm.toLowerCase())
              //   ) {
              //     return val;
              //   }
              // })
              productList.length
            }
            paginate={paginate}
            currentPage={currentPage}
          />
        </div>
      )}
    </>
  );
};
export default observer(AllProducts);